import { defineStore } from "pinia";
import { storage } from "@/storage";
import { STORAGE_KEYS } from "@/storage/keys";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
  getDocs,
  writeBatch,
  increment,
} from "firebase/firestore";
import { db } from "@/firebase/firestore";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  deleteUser
} from "firebase/auth";
import { auth } from "@/firebase";
import { fechaPagadoHasta, mesesVencidos } from "@/utils/suscripcion";
import { precioPorMeses, formatearMonto } from "@/constants/precios";

// Permite al router esperar a que Firebase Auth resuelva antes de navegar
let resolveAuthReady;
export const authReadyPromise = new Promise(resolve => {
  resolveAuthReady = resolve;
});

export const useUsuariosStore = defineStore("usuarios", {
  state: () => ({
    usuarioActual: null,
    usuarios: [], // Todos los usuarios registrados
    loading: false,
    error: null,
    notificacionesAdmin: [], // Nuevos registros pendientes de leer
    _unsubscribeNotificaciones: null, // Para poder cortar el listener al cerrar sesión
  }),

  getters: {
    // Verificar si usuario actual está autorizado
    usuarioAutorizado: (state) => {
      return state.usuarioActual?.autorizado === true;
    },

    // Verificar roles
    esAdmin: (state) => {
      return state.usuarioActual?.rol === 'admin';
    },

    esDoctor: (state) => {
      return state.usuarioActual?.rol === 'doctor';
    },

    // Verificar si tiene permisos (admin puede todo)
    tienePermiso: (state) => (permisos) => {
      if (!state.usuarioActual) return false;
      if (state.usuarioActual.rol === 'admin') return true;
      
      if (Array.isArray(permisos)) {
        return permisos.includes(state.usuarioActual.rol);
      }
      return state.usuarioActual.rol === permisos;
    },

    // Obtener estado de autorización
    estadoAutorizacion: (state) => {
      if (!state.usuarioActual) return null;
      return {
        autorizado: state.usuarioActual.autorizado,
        pagoPendiente: state.usuarioActual.pagoPendiente,
        fechaPago: state.usuarioActual.fechaPago,
        pagadoHasta: state.usuarioActual.pagadoHasta,
        observaciones: state.usuarioActual.observaciones,
        rol: state.usuarioActual.rol,
      };
    },

    // Obtener todos los usuarios pendientes de aprobación (ya no se usa, todos entran automáticamente)
    usuariosPendientes: (state) => {
      return state.usuarios.filter(u => !u.autorizado && !u.pagoPendiente);
    },

    // Obtener usuarios con pago pendiente (usuarios nuevos y los que no han pagado)
    usuariosPagoPendiente: (state) => {
      return state.usuarios.filter(u => u.autorizado && u.pagoPendiente);
    },

    // Obtener usuarios autorizados con acceso completo (ya pagaron)
    usuariosAutorizados: (state) => {
      return state.usuarios.filter(u => u.autorizado && !u.pagoPendiente);
    },

    // Bloqueados. Las dos listas de arriba exigen `autorizado`, así que sin este getter un
    // usuario bloqueado no aparecía en NINGUNA pantalla y no había forma de revertirlo.
    // El filtro es solo `!autorizado`: `rechazarUsuario` no toca `pagoPendiente`, así que un
    // bloqueado puede tenerlo en true o false. (Por eso tampoco sirve `usuariosPendientes`,
    // que además exige `!pagoPendiente` y se dejaría afuera a la mitad.)
    usuariosBloqueados: (state) => {
      return state.usuarios.filter(u => !u.autorizado);
    },
  },

  actions: {
    // 📝 REGISTRAR NUEVO USUARIO CON FIREBASE AUTH
    async registrarUsuario(datosRegistro) {
      try {
        this.loading = true;
        this.error = null;

        // Validar email
        if (!datosRegistro.email || !datosRegistro.email.includes('@')) {
          throw new Error("Email inválido");
        }

        // Validar que el usuario (nombre de usuario) no exista usando el índice público
        const usernameDoc = await getDoc(doc(db, "usernameIndex", datosRegistro.usuario));
        if (usernameDoc.exists()) {
          throw new Error("El nombre de usuario ya existe");
        }

        // 1. Crear usuario en Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          datosRegistro.email,
          datosRegistro.password
        );

        const firebaseUser = userCredential.user;

        // 2. Crear perfil en Firestore (con el UID de Firebase Auth)
        const nuevoUsuario = {
          uid: firebaseUser.uid, // UID de Firebase Auth
          usuario: datosRegistro.usuario,
          email: datosRegistro.email,
          nombre: datosRegistro.nombre,
          apellido: datosRegistro.apellido,
          telefono: datosRegistro.telefono,
          genero: datosRegistro.genero,
          rol: datosRegistro.rol || 'doctor', // Por defecto doctor
          autorizado: true, // Acceso inmediato
          pagoPendiente: true, // Requiere pago para modificar datos
          plan: 'mensual', // Plan por defecto; se actualiza al registrar/renovar según el plan pagado
          fechaPago: null,
          pagadoHasta: null,
          pruebaExtraDias: 0, // Días extra de prueba que un admin le puede sumar a mano
          observaciones: "Pago pendiente",
          notificacionesHabilitadas: false, // Deshabilitadas por defecto (versión Pro)
          googleCalendarHabilitado: false, // Deshabilitado por defecto (versión Pro)
          direccionTrabajo: '',
          fechaRegistro: serverTimestamp(),
          ultimoLogin: serverTimestamp(),
          ultimaActividad: serverTimestamp(),
        };

        // Perfil + índice de username en un solo writeBatch ATÓMICO: o se crean los dos, o
        // ninguno. Antes eran dos setDoc sueltos: si el segundo fallaba quedaba una cuenta de
        // Auth sin perfil (email secuestrado) o una cuenta sin login por username. (P1-4)
        try {
          const batch = writeBatch(db);
          batch.set(doc(db, "usuarios", firebaseUser.uid), nuevoUsuario);
          batch.set(doc(db, "usernameIndex", datosRegistro.usuario), { email: datosRegistro.email });
          await batch.commit();
        } catch (e) {
          // El perfil no se creó: borrar la cuenta de Auth recién creada para no dejar el email
          // ocupado para siempre, y avisar con un error claro para que pueda reintentar.
          console.warn("Falló la creación del perfil; se revierte la cuenta de Auth:", e);
          try {
            await deleteUser(firebaseUser);
          } catch (e2) {
            console.warn("No se pudo revertir la cuenta de Auth:", e2);
          }
          throw new Error("No se pudo completar el registro. Revisá tu conexión e intentá de nuevo.");
        }

        // 3. Guardar usuario en estado (login automático).
        // OJO: `serverTimestamp()` no tiene valor local (lo resuelve el servidor). Si dejáramos
        // ese "sobre cerrado" en la copia en memoria/localStorage, `usePagoVerificacion` haría
        // `new Date(sentinel)` → fecha inválida → NaN días desde el registro → creería que el
        // período de prueba terminó y le mostraría el modal de PAGO a una doctora recién
        // registrada, en pleno trial. Guardamos fechas reales localmente; el listener de Auth
        // traerá después los Timestamp autoritativos del servidor.
        const ahoraISO = new Date().toISOString();
        const usuarioLocal = {
          ...nuevoUsuario,
          id: firebaseUser.uid,
          fechaRegistro: ahoraISO,
          ultimoLogin: ahoraISO,
          ultimaActividad: ahoraISO,
        };
        this.usuarioActual = usuarioLocal;
        storage.set(STORAGE_KEYS.USUARIO_ACTUAL, usuarioLocal);

        // 4. Crear paciente demo para el usuario
        try {
          const { usePacientesStore } = await import("./pacientes");
          const pacientesStore = usePacientesStore();
          pacientesStore.agregarPaciente({
            nombre: "Paciente Demo",
            telefono: "",
            ci: "",
            alergias: "",
            observaciones: "",
            enfermedades: "",
          });
        } catch (e) {
          console.warn("No se pudo crear el paciente demo:", e);
        }

        // Notificar al admin sobre el nuevo registro
        try {
          await setDoc(doc(collection(db, "notificaciones_admin"), firebaseUser.uid), {
            tipo: 'nuevo_registro',
            nombre: `${datosRegistro.nombre} ${datosRegistro.apellido}`,
            usuario: datosRegistro.usuario,
            email: datosRegistro.email,
            telefono: datosRegistro.telefono || '',
            usuarioId: firebaseUser.uid,
            fecha: serverTimestamp(),
            leida: false,
          });
        } catch (e) {
          console.warn("No se pudo guardar notificación admin:", e);
        }

        return {
          exito: true,
          mensaje: "¡Registro exitoso! Acceso inmediato. Recuerda completar el pago para modificar datos.",
          usuarioId: firebaseUser.uid,
          usuario: usuarioLocal,
        };
      } catch (error) {
        // Manejar errores específicos de Firebase Auth
        let mensajeError = error.message;
        
        if (error.code === 'auth/email-already-in-use') {
          mensajeError = "El email ya está registrado";
        } else if (error.code === 'auth/weak-password') {
          mensajeError = "La contraseña debe tener al menos 6 caracteres";
        } else if (error.code === 'auth/invalid-email') {
          mensajeError = "Email inválido";
        }

        this.error = mensajeError;
        return {
          exito: false,
          mensaje: mensajeError,
        };
      } finally {
        this.loading = false;
      }
    },

    // 🔐 LOGIN CON FIREBASE AUTH
    async login(emailOUsuario, password) {
      try {
        this.loading = true;
        this.error = null;

        let email = emailOUsuario;

        // Si no es un email, buscar el email por nombre de usuario en el índice público
        if (!emailOUsuario.includes('@')) {
          const indexDoc = await getDoc(doc(db, "usernameIndex", emailOUsuario));

          if (!indexDoc.exists()) {
            throw new Error("Usuario no encontrado");
          }

          email = indexDoc.data().email;
        }

        // 1. Autenticar con Firebase Auth
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const firebaseUser = userCredential.user;

        // 2. Obtener datos del perfil desde Firestore
        const usuarioDoc = await getDoc(doc(db, "usuarios", firebaseUser.uid));

        if (!usuarioDoc.exists()) {
          await signOut(auth);
          throw new Error("Perfil de usuario no encontrado");
        }

        const usuarioData = usuarioDoc.data();
        usuarioData.id = usuarioDoc.id;

        // 3. Verificar si está autorizado
        if (!usuarioData.autorizado) {
          await signOut(auth);
          throw new Error("Usuario no autorizado. Contacta al administrador.");
        }

        // 4. Actualizar último login (y la marca de actividad: un login también es uso)
        await updateDoc(doc(db, "usuarios", firebaseUser.uid), {
          ultimoLogin: serverTimestamp(),
          ultimaActividad: serverTimestamp(),
        });

        // 5. Guardar usuario en estado
        this.usuarioActual = usuarioData;
        storage.set(STORAGE_KEYS.USUARIO_ACTUAL, usuarioData);

        return {
          exito: true,
          usuario: usuarioData,
        };
      } catch (error) {
        // Manejar errores específicos de Firebase Auth
        let mensajeError = error.message;
        
        if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
          mensajeError = "Usuario o contraseña incorrectos";
        } else if (error.code === 'auth/user-not-found') {
          mensajeError = "Usuario no encontrado";
        } else if (error.code === 'auth/too-many-requests') {
          mensajeError = "Demasiados intentos fallidos. Intenta más tarde.";
        } else if (error.code === 'auth/invalid-email') {
          mensajeError = "Email inválido";
        }

        this.error = mensajeError;
        return {
          exito: false,
          mensaje: mensajeError,
        };
      } finally {
        this.loading = false;
      }
    },

    // 📋 CARGAR TODOS LOS USUARIOS (Solo Admin)
    async cargarUsuarios() {
      try {
        this.loading = true;
        const usuariosRef = collection(db, "usuarios");
        const querySnapshot = await getDocs(usuariosRef);
        
        this.usuarios = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        return this.usuarios;
      } catch (error) {
        this.error = error.message;
        return [];
      } finally {
        this.loading = false;
      }
    },

    // ✅ AUTORIZAR USUARIO (Admin)
    async autorizarUsuario(usuarioId) {
      try {
        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          autorizado: true,
          pagoPendiente: false,
          observaciones: "Aprobado",
          fechaAprobacion: serverTimestamp(),
        });

        // Actualizar en estado local
        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.autorizado = true;
          usuario.pagoPendiente = false;
          usuario.observaciones = "Aprobado";
        }

        return { exito: true, mensaje: "Usuario autorizado" };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 💳 MARCAR PAGO PENDIENTE (Admin)
    async marcarPagoPendiente(usuarioId, observaciones = "") {
      try {
        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          pagoPendiente: true,
          autorizado: true, // Mantiene acceso para ver, pero no modificar
          observaciones: observaciones || "Pago pendiente",
          fechaPago: null,
          pagadoHasta: null,
        });

        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.pagoPendiente = true;
          usuario.autorizado = true;
          usuario.observaciones = observaciones || "Pago pendiente";
        }

        return { exito: true, mensaje: "Acceso concedido con pago pendiente" };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // ⏳ EXTENDER PERÍODO DE PRUEBA (Admin): suma días extra al trial sumándolos a
    // `pruebaExtraDias`. NO toca la fecha de alta (fechaRegistro), ni el estado de pago, ni
    // registra ningún cobro: es solo darle más aire a una doctora en prueba para que pueda
    // seguir cargando datos mientras decide/contrata. El trial total pasa a ser 7 + este valor
    // (ver `diasPruebaTotales` en usePagoVerificacion). Se usa `increment` para que sumar sea
    // atómico y no dependa de que el valor local esté fresco.
    async extenderPrueba(usuarioId, dias) {
      try {
        this.loading = true;
        const d = Number(dias);
        if (!Number.isFinite(d) || d <= 0) {
          return { exito: false, mensaje: "Cantidad de días inválida" };
        }
        const usuario = this.usuarios.find((u) => u.id === usuarioId);
        if (!usuario) return { exito: false, mensaje: "Usuario no encontrado" };

        await updateDoc(doc(db, "usuarios", usuarioId), {
          pruebaExtraDias: increment(d),
        });
        usuario.pruebaExtraDias = (Number(usuario.pruebaExtraDias) || 0) + d;

        return { exito: true, mensaje: `Prueba extendida ${d} ${d === 1 ? "día" : "días"}` };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Registra un cobro de suscripción de forma ATÓMICA: actualiza el usuario y deja el asiento
    // en `pagos_suscripcion`, ambos en el MISMO writeBatch (o los dos, o ninguno). Antes el cobro
    // solo pisaba `pagadoHasta` y no quedaba ningún rastro (monto, fecha, quién lo registró), así
    // que no había con qué emitir un comprobante ni saber cuánto se facturó.
    // `activarAcceso` = true en el alta (tab "Pago pendiente"); false en la renovación.
    async _registrarCobro(usuarioId, meses, datos = {}, activarAcceso = false) {
      const usuario = this.usuarios.find((u) => u.id === usuarioId);
      if (!usuario) return { exito: false, mensaje: "Usuario no encontrado" };

      const ahora = new Date();
      // Sumar SIEMPRE desde la última fecha cubierta, esté vencida o no: así la deuda se acumula
      // y no se perdona sola. (Antes el alta hacía `hoy + N meses` y borraba los meses adeudados.)
      const base = fechaPagadoHasta(usuario) || ahora;
      const nuevaFecha = new Date(base);
      nuevaFecha.setMonth(nuevaFecha.getMonth() + meses);

      const plan = Number(meses) === 12 ? "anual" : "mensual";
      const monto = Number(datos.monto) > 0 ? Number(datos.monto) : precioPorMeses(meses);
      const adminId = this.usuarioActual?.id || this.usuarioActual?.uid || null;

      // Campos para la nube (serverTimestamp) y para la copia local (fecha real, no el sentinel).
      const camposNube = { pagadoHasta: nuevaFecha, plan, observaciones: "Pago recibido" };
      const camposLocal = { ...camposNube };
      if (activarAcceso) {
        camposNube.autorizado = true;
        camposNube.pagoPendiente = false;
        camposNube.fechaAprobacion = serverTimestamp();
        camposLocal.autorizado = true;
        camposLocal.pagoPendiente = false;
        camposLocal.fechaAprobacion = ahora;
      }

      const pagoId = crypto.randomUUID();
      const batch = writeBatch(db);
      batch.update(doc(db, "usuarios", usuarioId), camposNube);
      batch.set(doc(db, "pagos_suscripcion", pagoId), {
        id: pagoId,
        usuarioId,
        monto,
        meses: Number(meses),
        plan,
        metodoPago: datos.metodoPago || "",
        observacion: String(datos.observacion || "").trim(),
        fecha: serverTimestamp(),
        registradoPor: adminId,
        // Qué período cubre este cobro: arranca en el mes más viejo adeudado (la fecha base),
        // no en "hoy". Así el recibo puede decir "1 mes — mayo 2026" y no un genérico "1 mes".
        periodoDesde: base,
        pagadoHastaResultante: nuevaFecha,
      });
      await batch.commit();

      // Reflejar en local recién cuando la nube confirmó.
      Object.assign(usuario, camposLocal);

      return { exito: true, usuario, base, nuevaFecha, plan, monto };
    },

    // 💰 REGISTRAR PAGO Y AUTORIZAR (Admin). `meses` = duración del plan pagado (1 o 12).
    // `datos` = { monto, observacion, metodoPago } — todos opcionales.
    async registrarPagoYAutorizar(usuarioId, meses = 1, datos = {}) {
      try {
        this.loading = true;
        const r = await this._registrarCobro(usuarioId, meses, datos, true);
        if (!r.exito) return r;
        const etiqueta = Number(meses) === 12 ? "anual" : "mensual";
        return {
          exito: true,
          mensaje: `Pago ${etiqueta} de ${formatearMonto(r.monto)} registrado. Cubre hasta el ${r.nuevaFecha.toLocaleDateString("es-UY")}`,
        };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 🔄 RENOVAR SUSCRIPCIÓN (suma N meses a pagadoHasta sin tocar autorizado ni pagoPendiente).
    // Siempre suma desde la última fecha cubierta (pagadoHasta), esté vencida o no: así la deuda
    // se acumula y no se perdona sola. Si debía 3 meses y registrás 1, sigue debiendo 2; hay que
    // registrar cada mes atrasado. Solo cae a `ahora` si nunca hubo un pago previo (sin pagadoHasta).
    async renovarSuscripcion(usuarioId, meses = 1, datos = {}) {
      try {
        this.loading = true;
        const r = await this._registrarCobro(usuarioId, meses, datos, false);
        if (!r.exito) return r;

        // Cuánto sigue debiendo tras este pago (0 = al día). Refleja el modelo acumulativo.
        const restante = mesesVencidos(r.usuario);
        const fechaTxt = r.nuevaFecha.toLocaleDateString('es-UY');
        const colaDeuda = restante > 0
          ? ` Todavía debe ${restante} mes${restante === 1 ? '' : 'es'}.`
          : ` Queda al día (vence el ${fechaTxt}).`;

        let mensaje;
        if (Number(meses) === 1) {
          // El pago mensual cubre el mes que arrancaba en la fecha base (el más viejo adeudado).
          const mesPagado = r.base.toLocaleDateString('es-UY', { month: 'long', year: 'numeric' });
          mensaje = `Registraste el pago de ${mesPagado} (${formatearMonto(r.monto)}).${colaDeuda}`;
        } else {
          mensaje = `Pago de 12 meses (${formatearMonto(r.monto)}) registrado.${colaDeuda}`;
        }
        return { exito: true, mensaje };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 🧾 Cargar un cobro YA REALIZADO (histórico), solo para poder emitir su comprobante.
    // A diferencia de `_registrarCobro`, esto NO toca al usuario: no mueve `pagadoHasta` ni el
    // estado de pago. Sirve para los pagos hechos antes de que existiera el libro (si usaras el
    // botón normal, le sumarías un mes de regalo). `datos.mesAbonado` viene como "YYYY-MM".
    async registrarCobroHistorico(usuarioId, datos = {}) {
      try {
        this.loading = true;
        const usuario = this.usuarios.find((u) => u.id === usuarioId);
        if (!usuario) return { exito: false, mensaje: "Usuario no encontrado" };

        const meses = Number(datos.meses) === 12 ? 12 : 1;
        const plan = meses === 12 ? "anual" : "mensual";
        const monto = Number(datos.monto) > 0 ? Number(datos.monto) : precioPorMeses(meses);

        // Fechas al mediodía local, para que no se corran de día por zona horaria.
        const fechaPago = datos.fecha ? new Date(`${datos.fecha}T12:00:00`) : new Date();
        if (isNaN(fechaPago.getTime())) {
          return { exito: false, mensaje: "La fecha del pago no es válida." };
        }
        const periodoDesde = datos.mesAbonado ? new Date(`${datos.mesAbonado}-01T12:00:00`) : null;
        if (!periodoDesde || isNaN(periodoDesde.getTime())) {
          return { exito: false, mensaje: "Indicá el mes abonado." };
        }
        const pagadoHastaResultante = new Date(periodoDesde);
        pagadoHastaResultante.setMonth(pagadoHastaResultante.getMonth() + meses);

        const pagoId = crypto.randomUUID();
        const adminId = this.usuarioActual?.id || this.usuarioActual?.uid || null;

        await setDoc(doc(db, "pagos_suscripcion", pagoId), {
          id: pagoId,
          usuarioId,
          monto,
          meses,
          plan,
          metodoPago: datos.metodoPago || "",
          observacion: String(datos.observacion || "").trim(),
          fecha: fechaPago,                 // cuándo pagó de verdad (no el momento de la carga)
          periodoDesde,
          pagadoHastaResultante,
          registradoPor: adminId,
          registradoEn: serverTimestamp(),  // cuándo se cargó este asiento a mano
          historico: true,
        });

        return {
          exito: true,
          mensaje: `Cobro histórico de ${formatearMonto(monto)} cargado. No se modificó el vencimiento.`,
        };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 📒 Historial de cobros de una doctora (solo admin, según las reglas de Firestore).
    // Se ordena en el cliente por fecha desc para no requerir un índice compuesto.
    async cargarPagosDeUsuario(usuarioId) {
      try {
        const snap = await getDocs(
          query(collection(db, "pagos_suscripcion"), where("usuarioId", "==", usuarioId))
        );
        const pagos = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        const aMs = (f) => (f?.toDate ? f.toDate().getTime() : (f?.seconds ? f.seconds * 1000 : 0));
        pagos.sort((a, b) => aMs(b.fecha) - aMs(a.fecha));
        return pagos;
      } catch (e) {
        console.warn("No se pudieron cargar los pagos de suscripción:", e);
        return [];
      }
    },

    // 🚫 REVOCAR ACCESO DE PAGO (No bloquea login, solo permisos de modificación)
    async revocarAccesoPago(usuarioId) {
      try {
        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          pagoPendiente: true,
          fechaPago: null,
          pagadoHasta: null,
          observaciones: "Acceso revocado - Pago requerido",
        });

        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.pagoPendiente = true;
          usuario.fechaPago = null;
          usuario.pagadoHasta = null;
          usuario.observaciones = "Acceso revocado - Pago requerido";
        }

        return { exito: true, mensaje: "Acceso de modificación revocado. Usuario puede seguir viendo la app." };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 🗑️ RECHAZAR/BLOQUEAR USUARIO COMPLETAMENTE
    async rechazarUsuario(usuarioId) {
      try {
        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          autorizado: false,
          activo: false,
          observaciones: "Usuario bloqueado",
        });

        // Se actualiza EN EL LUGAR, no se saca de la lista. Antes hacía
        // `filter(u => u.id !== usuarioId)`: el usuario desaparecía del array en memoria y ya no
        // había forma de verlo ni de desbloquearlo hasta recargar. Ahora cae en el tab
        // "Bloqueados" (getter `usuariosBloqueados`) apenas se confirma.
        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.autorizado = false;
          usuario.activo = false;
          usuario.observaciones = "Usuario bloqueado";
        }

        return { exito: true, mensaje: "Usuario bloqueado completamente" };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 🔓 DESBLOQUEAR USUARIO (Admin) — revierte `rechazarUsuario`
    //
    // NO reutiliza `autorizarUsuario` a propósito: esa acción además escribe
    // `pagoPendiente: false` + `observaciones: "Aprobado"` + `fechaAprobacion`, o sea que
    // desbloquear a alguien que estaba en período de prueba lo marcaría como que ya pagó.
    // Desbloquear devuelve el acceso y nada más; el estado de pago queda como estaba.
    async desbloquearUsuario(usuarioId) {
      try {
        this.loading = true;
        const usuario = this.usuarios.find(u => u.id === usuarioId);

        // "Usuario bloqueado" ya no describe la situación; se vuelve a la observación que
        // corresponde según deba o no pagar.
        const observaciones = usuario?.pagoPendiente ? "Pago pendiente" : "Aprobado";

        await updateDoc(doc(db, "usuarios", usuarioId), {
          autorizado: true,
          activo: true,
          observaciones,
        });

        if (usuario) {
          usuario.autorizado = true;
          usuario.activo = true;
          usuario.observaciones = observaciones;
        }

        return { exito: true, mensaje: "Usuario desbloqueado" };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 🗑️ `eliminarUsuarioCompleto` se quitó a propósito (era el P0 de la fase 6 de la auditoría).
    // Desde el cliente NO se puede borrar de verdad a una doctora:
    //   - Las reglas solo permiten borrar pacientes/turnos/historia a su DUEÑO, no al admin: esos
    //     deletes eran rechazados, un `catch` con `console.warn` se los tragaba y seguía de largo.
    //   - Los archivos de Storage nunca se tocaban.
    //   - La cuenta de Auth requiere el Admin SDK; solo quedaba una nota diciendo "borrala a mano".
    // Peor aún: el borrado del perfil (`usuarios/{id}`) SÍ está permitido y no tenía catch, así
    // que la acción borraba el índice y dejaba huérfanos todos los datos clínicos, y aun así
    // reportaba "Usuario eliminado completamente".
    // Para bajar una cuenta hoy: "Bloquear Completamente". El borrado real necesita una Cloud
    // Function con el Admin SDK que baje perfil + pacientes + historia + turnos + Storage + Auth.

    // � CAMBIAR ROL DE USUARIO (Solo Admin)
    async cambiarRol(usuarioId, nuevoRol) {
      try {
        // Validar que el usuario actual sea admin
        if (this.usuarioActual?.rol !== 'admin') {
          throw new Error("Solo los administradores pueden cambiar roles");
        }

        // Validar rol
        const rolesValidos = ['admin', 'doctor'];
        if (!rolesValidos.includes(nuevoRol)) {
          throw new Error("Rol inválido");
        }

        // Nadie puede sacarse el admin a sí mismo. La validación de arriba no lo cubre: pregunta
        // si QUIEN LLAMA es admin, y todavía lo es. Pero al escribir `rol: 'doctor'` en su propio
        // documento, el router lo echa de /admin y `esAdmin()` de las reglas de Firestore empieza a
        // fallar → no puede revertirlo desde la app. Si además era el único admin, la instalación
        // queda sin ninguno y solo se recupera desde la consola de Firebase.
        //
        // Con esto alcanza para garantizar que SIEMPRE quede al menos un admin: para degradar a
        // otro hay que ser admin, y al hacerlo uno sigue siéndolo. El único camino a cero admins
        // era este.
        const propioId = this.usuarioActual?.id || this.usuarioActual?.uid;
        if (usuarioId === propioId && nuevoRol !== 'admin') {
          throw new Error("No podés quitarte el rol de administrador a vos mismo. Si querés dejar de ser admin, primero nombrá a otro y pedile que te lo cambie.");
        }

        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          rol: nuevoRol,
        });

        // Actualizar en estado local
        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.rol = nuevoRol;
        }

        return { exito: true, mensaje: `Rol actualizado a ${nuevoRol}` };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // �🚪 LOGOUT CON FIREBASE AUTH
    // ACTUALIZAR PERFIL DEL USUARIO ACTUAL
    async actualizarPerfil(datos) {
      try {
        const uid = this.usuarioActual?.id || this.usuarioActual?.uid;
        if (!uid) throw new Error('No hay usuario autenticado');
        await updateDoc(doc(db, 'usuarios', uid), datos);
        Object.assign(this.usuarioActual, datos);
        storage.set(STORAGE_KEYS.USUARIO_ACTUAL, this.usuarioActual);
        return { exito: true };
      } catch (error) {
        return { exito: false, mensaje: error.message };
      }
    },

    async logout() {
      try {
        // Detener listeners antes de cerrar sesión para evitar callbacks con usuario nulo
        const { usePacientesStore } = await import("./pacientes");
        const { useTurnosStore } = await import("./turnos");
        usePacientesStore().detenerEscucha();
        useTurnosStore().detenerEscucha();
        // Este quedaba vivo: al no haber sesión admin, las reglas lo rechazaban y tiraba
        // "Uncaught Error in snapshot listener: permission-denied" (Fase 4 P2 de la auditoría).
        this.detenerEscuchaNotificaciones();
        await signOut(auth);
        this.usuarioActual = null;
        this.usuarios = [];
        this.notificacionesAdmin = [];
        storage.remove(STORAGE_KEYS.USUARIO_ACTUAL);
        return { exito: true };
      } catch (error) {
        console.error("Error al cerrar sesión:", error);
        return { exito: false, mensaje: error.message };
      }
    },

    // 🔑 RECUPERAR CONTRASEÑA
    async recuperarContrasena(email) {
      try {
        this.loading = true;
        await sendPasswordResetEmail(auth, email);
        return {
          exito: true,
          mensaje: "Se envió un correo para restablecer la contraseña",
        };
      } catch (error) {
        let mensajeError = error.message;
        
        if (error.code === 'auth/user-not-found') {
          mensajeError = "No existe un usuario con ese email";
        } else if (error.code === 'auth/invalid-email') {
          mensajeError = "Email inválido";
        }

        return {
          exito: false,
          mensaje: mensajeError,
        };
      } finally {
        this.loading = false;
      }
    },

    // 🟢 MARCAR ACTIVIDAD (se llama en cada apertura de la app)
    //
    // `ultimoLogin` solo se escribe cuando alguien tipea usuario y contraseña, y Firebase Auth
    // mantiene la sesión viva indefinidamente (persistencia en IndexedDB + refresh token que se
    // renueva solo). Resultado: una doctora que usa la app todos los días desde el mismo celular
    // figuraba en el panel admin con "último acceso" de hace meses, mientras que la que se olvidó
    // la contraseña figuraba como activa — la señal justo al revés de la que sirve para decidir
    // a quién retener. `ultimaActividad` mide aperturas reales de la app.
    async marcarActividad(usuarioId, actividadPrevia) {
      // Throttle contra el documento del servidor (NO contra localStorage): si ya se marcó hoy,
      // no se escribe. Sale 1 write por usuario por día y se comporta bien con varios
      // dispositivos (si abrió en el celular a la mañana, la compu a la tarde no reescribe).
      const previa = actividadPrevia?.toDate?.() ??
        (actividadPrevia ? new Date(actividadPrevia) : null);
      if (previa && !isNaN(previa) && previa.toDateString() === new Date().toDateString()) {
        return;
      }

      try {
        await updateDoc(doc(db, "usuarios", usuarioId), {
          ultimaActividad: serverTimestamp(),
        });

        // OJO: `serverTimestamp()` es un sentinel sin valor local. Guardarlo en la copia en
        // memoria/localStorage haría que cualquier `new Date(...)` sobre él diera fecha
        // inválida (el mismo pisón ya documentado en `registrarUsuario`). Localmente va un
        // Date real; el Timestamp autoritativo del servidor llega en la próxima lectura.
        if (this.usuarioActual?.id === usuarioId) {
          this.usuarioActual = {
            ...this.usuarioActual,
            ultimaActividad: new Date().toISOString(),
          };
          storage.set(STORAGE_KEYS.USUARIO_ACTUAL, this.usuarioActual);
        }
      } catch (e) {
        // Es un dato de telemetría: si falla (offline, permisos) no debe romper el arranque.
        console.warn("No se pudo marcar la actividad:", e);
      }
    },

    // 👂 OBSERVAR ESTADO DE AUTENTICACIÓN
    inicializarAuthListener() {
      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          // Usuario autenticado en Firebase
          try {
            const usuarioDoc = await getDoc(doc(db, "usuarios", firebaseUser.uid));
            if (usuarioDoc.exists()) {
              const usuarioData = usuarioDoc.data();
              usuarioData.id = usuarioDoc.id;

              // Solo establecer si está autorizado
              if (usuarioData.autorizado) {
                this.usuarioActual = usuarioData;
                storage.set(STORAGE_KEYS.USUARIO_ACTUAL, usuarioData);

                // Marca de uso real. Sin `await`: es telemetría, no debe demorar el arranque
                // ni el guard del router (tiene su propio try/catch adentro).
                this.marcarActividad(usuarioData.id, usuarioData.ultimaActividad);

                // Si es admin, iniciar escucha de nuevos registros
                if (usuarioData.rol === 'admin') {
                  this.iniciarEscuchaNotificaciones();
                }
              } else {
                // No autorizado, cerrar sesión
                await signOut(auth);
                this.usuarioActual = null;
                storage.remove(STORAGE_KEYS.USUARIO_ACTUAL);
              }
            }
          } catch (error) {
            console.error("Error al obtener datos del usuario:", error);
          }
        } else {
          // No autenticado
          this.usuarioActual = null;
          storage.remove(STORAGE_KEYS.USUARIO_ACTUAL);
        }
        resolveAuthReady();
      });
    },

    // 🔔 ESCUCHAR NUEVOS REGISTROS EN TIEMPO REAL (Solo Admin)
    iniciarEscuchaNotificaciones() {
      // Cortar el anterior: sin esto se acumulaba un listener por cada login.
      this.detenerEscuchaNotificaciones();

      const q = query(
        collection(db, "notificaciones_admin"),
        where("leida", "==", false)
      );

      this._unsubscribeNotificaciones = onSnapshot(
        q,
        (snapshot) => {
          this.notificacionesAdmin = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

          // Mostrar notificación del navegador por cada nuevo registro que llega
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const data = change.doc.data();
              // Evitar notificar en la carga inicial (solo doc con fecha reciente)
              const fecha = data.fecha?.toDate?.();
              const hace10Seg = new Date(Date.now() - 10000);
              if (fecha && fecha > hace10Seg) {
                this._mostrarNotificacionNavegador(data);
              }
            }
          });
        },
        // Sin este manejador, el error salía como "Uncaught Error in snapshot listener".
        // Solo el admin puede leer notificaciones_admin: si la sesión ya no es admin (o se
        // cerró), Firestore devuelve permission-denied. Cortamos el listener en vez de dejarlo
        // reintentando contra una regla que nunca va a pasar.
        (e) => {
          console.warn("onSnapshot notificaciones_admin falló, se detiene:", e);
          this.detenerEscuchaNotificaciones();
          this.notificacionesAdmin = [];
        }
      );
    },

    detenerEscuchaNotificaciones() {
      if (this._unsubscribeNotificaciones) {
        this._unsubscribeNotificaciones();
        this._unsubscribeNotificaciones = null;
      }
    },

    _mostrarNotificacionNavegador(data) {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') this._mostrarNotificacionNavegador(data);
        });
        return;
      }
      const notif = new Notification('🆕 Nuevo registro en Vedent', {
        body: `${data.nombre} (${data.usuario})\n${data.email}${data.telefono ? ' · ' + data.telefono : ''}`,
        icon: '/icons/icon-192.png',
        tag: `registro-${data.usuarioId}`,
      });
      notif.onclick = () => { window.focus(); notif.close(); };
      setTimeout(() => notif.close(), 15000);
    },

    // ✅ MARCAR NOTIFICACIONES COMO LEÍDAS (Admin)
    async marcarNotificacionesLeidas() {
      try {
        const q = query(
          collection(db, "notificaciones_admin"),
          where("leida", "==", false)
        );
        const snapshot = await getDocs(q);
        const updates = snapshot.docs.map(d => updateDoc(doc(db, "notificaciones_admin", d.id), { leida: true }));
        await Promise.all(updates);
        this.notificacionesAdmin = [];
      } catch (e) {
        console.warn("No se pudo marcar notificaciones como leídas:", e);
      }
    },

    // 💾 RESTAURAR SESIÓN DESDE STORAGE
    restaurarSesion() {
      const usuarioGuardado = storage.get(STORAGE_KEYS.USUARIO_ACTUAL);
      if (usuarioGuardado) {
        this.usuarioActual = usuarioGuardado;
        return true;
      }
      return false;
    },

    // 🔔 HABILITAR/DESHABILITAR NOTIFICACIONES (Admin)
    async toggleNotificaciones(usuarioId, habilitar) {
      try {
        // Validar que el usuario actual sea admin
        if (this.usuarioActual?.rol !== 'admin') {
          throw new Error("Solo los administradores pueden gestionar notificaciones");
        }

        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          notificacionesHabilitadas: habilitar,
        });

        // Actualizar en estado local
        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.notificacionesHabilitadas = habilitar;
        }

        // Si es el usuario actual, actualizar también
        if (this.usuarioActual?.id === usuarioId) {
          this.usuarioActual.notificacionesHabilitadas = habilitar;
          storage.set(STORAGE_KEYS.USUARIO_ACTUAL, this.usuarioActual);
        }

        return { 
          exito: true, 
          mensaje: `Notificaciones ${habilitar ? 'habilitadas' : 'deshabilitadas'}` 
        };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 📅 HABILITAR/DESHABILITAR GOOGLE CALENDAR (Admin)
    async toggleGoogleCalendar(usuarioId, habilitar) {
      try {
        // Validar que el usuario actual sea admin
        if (this.usuarioActual?.rol !== 'admin') {
          throw new Error("Solo los administradores pueden gestionar Google Calendar");
        }

        this.loading = true;
        await updateDoc(doc(db, "usuarios", usuarioId), {
          googleCalendarHabilitado: habilitar,
        });

        // Actualizar en estado local
        const usuario = this.usuarios.find(u => u.id === usuarioId);
        if (usuario) {
          usuario.googleCalendarHabilitado = habilitar;
        }

        // Si es el usuario actual, actualizar también
        if (this.usuarioActual?.id === usuarioId) {
          this.usuarioActual.googleCalendarHabilitado = habilitar;
          storage.set(STORAGE_KEYS.USUARIO_ACTUAL, this.usuarioActual);
        }

        return { 
          exito: true, 
          mensaje: `Google Calendar ${habilitar ? 'habilitado' : 'deshabilitado'}` 
        };
      } catch (error) {
        this.error = error.message;
        return { exito: false, mensaje: error.message };
      } finally {
        this.loading = false;
      }
    },
  },
});
